/**
 * 响应式工具函数
 * 提供设备检测、触摸优化、视口管理等功能
 */

// 设备类型检测
export const getDeviceType = () => {
  const width = window.innerWidth;
  if (width <= 768) return 'mobile';
  if (width <= 1024) return 'tablet';
  return 'desktop';
};

// 是否为触摸设备
export const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

// 是否为移动设备
export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// 获取视口信息
export const getViewportInfo = () => {
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio || 1,
    orientation: window.innerHeight > window.innerWidth ? 'portrait' : 'landscape'
  };
};

// 防抖函数
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// 节流函数
export const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// 触摸事件优化
export const addTouchOptimization = (element) => {
  if (!element) return;
  
  // 添加触摸反馈
  element.addEventListener('touchstart', (e) => {
    element.style.transform = 'scale(0.95)';
    element.style.transition = 'transform 0.1s ease';
  }, { passive: true });
  
  element.addEventListener('touchend', (e) => {
    element.style.transform = 'scale(1)';
  }, { passive: true });
  
  element.addEventListener('touchcancel', (e) => {
    element.style.transform = 'scale(1)';
  }, { passive: true });
};

// 视口变化监听
export const addViewportListener = (callback) => {
  const debouncedCallback = debounce(callback, 100);
  
  window.addEventListener('resize', debouncedCallback);
  window.addEventListener('orientationchange', debouncedCallback);
  
  return () => {
    window.removeEventListener('resize', debouncedCallback);
    window.removeEventListener('orientationchange', debouncedCallback);
  };
};

// 滚动优化
export const addScrollOptimization = () => {
  // 平滑滚动
  document.documentElement.style.scrollBehavior = 'smooth';
  
  // 防止iOS橡皮筋效果
  document.body.style.overscrollBehavior = 'contain';
};

// 键盘导航优化
export const addKeyboardNavigation = () => {
  document.addEventListener('keydown', (e) => {
    // ESC键关闭模态框或返回
    if (e.key === 'Escape') {
      const modals = document.querySelectorAll('.modal, .overlay');
      modals.forEach(modal => {
        if (modal.style.display !== 'none') {
          modal.style.display = 'none';
        }
      });
    }
    
    // 回车键提交表单
    if (e.key === 'Enter' && e.target.classList.contains('form-input')) {
      const form = e.target.closest('form');
      if (form) {
        const submitBtn = form.querySelector('button[type="submit"]');
        if (submitBtn && !submitBtn.disabled) {
          submitBtn.click();
        }
      }
    }
  });
};

// 性能优化
export const addPerformanceOptimization = () => {
  // 图片懒加载
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            observer.unobserve(img);
          }
        }
      });
    });
    
    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }
};

// 初始化响应式优化
export const initResponsiveOptimization = () => {
  addScrollOptimization();
  addKeyboardNavigation();
  addPerformanceOptimization();
  
  // 添加设备类名到body
  const deviceType = getDeviceType();
  document.body.classList.add(`device-${deviceType}`);
  
  if (isTouchDevice()) {
    document.body.classList.add('touch-device');
  }
  
  if (isMobileDevice()) {
    document.body.classList.add('mobile-device');
  }
  
  // 监听视口变化
  addViewportListener(() => {
    const newDeviceType = getDeviceType();
    document.body.className = document.body.className.replace(/device-\w+/, `device-${newDeviceType}`);
  });
};

// 触摸手势支持
export const addGestureSupport = (element, callbacks = {}) => {
  if (!element) return;
  
  let startX, startY, startTime;
  
  element.addEventListener('touchstart', (e) => {
    const touch = e.touches[0];
    startX = touch.clientX;
    startY = touch.clientY;
    startTime = Date.now();
  }, { passive: true });
  
  element.addEventListener('touchend', (e) => {
    if (!startX || !startY) return;
    
    const touch = e.changedTouches[0];
    const endX = touch.clientX;
    const endY = touch.clientY;
    const endTime = Date.now();
    
    const deltaX = endX - startX;
    const deltaY = endY - startY;
    const deltaTime = endTime - startTime;
    
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    // eslint-disable-next-line no-unused-vars
    const velocity = distance / deltaTime;
    
    // 滑动检测
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX > 0 && callbacks.onSwipeRight) {
        callbacks.onSwipeRight();
      } else if (deltaX < 0 && callbacks.onSwipeLeft) {
        callbacks.onSwipeLeft();
      }
    }
    
    // 点击检测
    if (distance < 10 && deltaTime < 300) {
      if (callbacks.onTap) {
        callbacks.onTap();
      }
    }
    
    // 长按检测
    if (distance < 10 && deltaTime > 500) {
      if (callbacks.onLongPress) {
        callbacks.onLongPress();
      }
    }
    
    startX = startY = null;
  }, { passive: true });
};

const responsiveUtils = {
  getDeviceType,
  isTouchDevice,
  isMobileDevice,
  getViewportInfo,
  debounce,
  throttle,
  addTouchOptimization,
  addViewportListener,
  addScrollOptimization,
  addKeyboardNavigation,
  addPerformanceOptimization,
  initResponsiveOptimization,
  addGestureSupport
};

export default responsiveUtils;
